import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { validatePhoneNumber, formatPhoneNumberInput } from '../../utils/phoneValidation';
import { BIRTH_MONTHS } from '../../utils/profileFields';
import './NewUserPage.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

interface UserFormData {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  zipCode: string;
  birthMonth: string;
  emergencyFirstName: string;
  emergencyLastName: string;
  emergencyRelationship: string;
  emergencyPhoneNumber: string;
  liabilityWaiverAccepted: boolean;
}

const NewUserPage: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<UserFormData>({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    zipCode: '',
    birthMonth: '',
    emergencyFirstName: '',
    emergencyLastName: '',
    emergencyRelationship: '',
    emergencyPhoneNumber: '',
    liabilityWaiverAccepted: false
  });
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [generatedPin, setGeneratedPin] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    let processedValue = value;
    
    // Handle phone number formatting (but not validation)
    if (name === 'phoneNumber' || name === 'emergencyPhoneNumber') {
      processedValue = formatPhoneNumberInput(value);
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : processedValue
    }));
  };


  const validateForm = () => {
    if (!formData.firstName.trim()) {
      setMessage('First name is required');
      return false;
    }
    if (!formData.lastName.trim()) {
      setMessage('Last name is required');
      return false;
    }
    if (!formData.email.trim()) {
      setMessage('Email is required');
      return false;
    }
    if (!formData.phoneNumber.trim()) {
      setMessage('Phone number is required');
      return false;
    }
    
    // Validate phone number format
    const phoneValidation = validatePhoneNumber(formData.phoneNumber);
    if (!phoneValidation.isValid) {
      setMessage('Phone number: ' + (phoneValidation.error || 'Invalid format'));
      return false;
    }
    // Zip code and birth month are optional, no validation needed
    if (!formData.emergencyFirstName.trim()) {
      setMessage('Emergency contact first name is required');
      return false;
    }
    if (!formData.emergencyLastName.trim()) {
      setMessage('Emergency contact last name is required');
      return false;
    }
    if (!formData.emergencyRelationship.trim()) {
      setMessage('Emergency contact relationship is required');
      return false;
    }
    if (!formData.emergencyPhoneNumber.trim()) {
      setMessage('Emergency contact phone number is required');
      return false;
    }
    
    // Validate emergency phone number format
    const emergencyPhoneValidation = validatePhoneNumber(formData.emergencyPhoneNumber);
    if (!emergencyPhoneValidation.isValid) {
      setMessage('Emergency phone number: ' + (emergencyPhoneValidation.error || 'Invalid format'));
      return false;
    }
    if (!formData.liabilityWaiverAccepted) {
      setMessage('You must accept the liability waiver to register');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setMessage('');

    try {
      const phoneValidation = validatePhoneNumber(formData.phoneNumber);
      const emergencyPhoneValidation = validatePhoneNumber(formData.emergencyPhoneNumber);
      const response = await fetch(`${API_BASE_URL}/register-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          phoneNumber: phoneValidation.formatted,
          emergencyPhoneNumber: emergencyPhoneValidation.formatted,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setGeneratedPin(data.passcode);
        setMessage(`Registration successful! Your PIN is: ${data.passcode}`);
      } else {
        setMessage(data.message || 'Registration failed');
      }
    } catch (error) {
      setMessage('Failed to connect to server');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/');
  };

  const handleLoginWithPin = () => {
    if (generatedPin) {
      localStorage.setItem('currentPasscode', generatedPin);
      localStorage.setItem('canAddDependents', 'true');
      navigate('/success', { state: { passcode: generatedPin, canAddDependents: true } });
    }
  };

  return (
    <div className="new-user-container">
      <div className="new-user-card">
        <div className="new-user-header">
          <div className="logo">
            <img src="/VolunteerHeart.png" alt="FabMo Volunteer" className="logo-image" />
          </div>
          <h1 className="new-user-title">New User Registration</h1>
          <p className="new-user-subtitle">
            Create a new account to access FabMo Volunteer App
          </p>
          <div className="form-hints">
            <p><span className="required-star">*</span> Required field</p>
            <p><strong>Zip Code:</strong> Optional. Leave blank to opt out.</p>
            <p><strong>Birth Month:</strong> Optional. Choose Opt out if you prefer not to share it.</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="registration-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName">First Name <span className="required-star">*</span></label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                required
                className="form-input"
                placeholder="Enter first name"
              />
            </div>
            <div className="form-group">
              <label htmlFor="lastName">Last Name <span className="required-star">*</span></label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                required
                className="form-input"
                placeholder="Enter last name"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="email">Email Address <span className="required-star">*</span></label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              className="form-input"
              placeholder="Enter email address"
            />
          </div>

          <div className="form-group">
            <label htmlFor="phoneNumber">Phone Number <span className="required-star">*</span></label>
            <input
              type="tel"
              id="phoneNumber"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleInputChange}
              required
              className="form-input"
              placeholder="(555)555-5555"
            />
          </div>

          <div className="form-group">
            <label htmlFor="zipCode">Zip Code (Optional)</label>
            <input
              type="text"
              id="zipCode"
              name="zipCode"
              value={formData.zipCode}
              onChange={handleInputChange}
              className="form-input"
              placeholder="Leave blank to opt out"
            />
          </div>

          <div className="form-group">
            <label htmlFor="birthMonth">Birth Month (Optional)</label>
            <select
              id="birthMonth"
              name="birthMonth"
              value={formData.birthMonth}
              onChange={handleInputChange}
              className="form-select"
            >
              <option value="">Opt out</option>
              {BIRTH_MONTHS.map((month) => (
                <option key={month.value} value={month.value}>{month.label}</option>
              ))}
            </select>
          </div>


          <div className="form-divider">
            <div className="divider-line"></div>
            <span className="divider-text">Emergency Contact Information</span>
            <div className="divider-line"></div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="emergencyFirstName">First Name <span className="required-star">*</span></label>
              <input
                type="text"
                id="emergencyFirstName"
                name="emergencyFirstName"
                value={formData.emergencyFirstName}
                onChange={handleInputChange}
                required
                className="form-input"
                placeholder="Enter first name"
              />
            </div>
            <div className="form-group">
              <label htmlFor="emergencyLastName">Last Name <span className="required-star">*</span></label>
              <input
                type="text"
                id="emergencyLastName"
                name="emergencyLastName"
                value={formData.emergencyLastName}
                onChange={handleInputChange}
                required
                className="form-input"
                placeholder="Enter last name"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="emergencyRelationship">Relationship <span className="required-star">*</span></label>
              <select
                id="emergencyRelationship"
                name="emergencyRelationship"
                value={formData.emergencyRelationship}
                onChange={handleInputChange}
                required
                className="form-select"
              >
                <option value="">Select Relationship</option>
                <option value="Parent">Parent</option>
                <option value="Guardian">Guardian</option>
                <option value="Spouse">Spouse</option>
                <option value="Sibling">Sibling</option>
                <option value="Child">Child</option>
                <option value="Friend">Friend</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="emergencyPhoneNumber">Phone Number <span className="required-star">*</span></label>
              <input
                type="tel"
                id="emergencyPhoneNumber"
                name="emergencyPhoneNumber"
                value={formData.emergencyPhoneNumber}
                onChange={handleInputChange}
                required
                className="form-input"
                placeholder="(555)555-5555"
              />
            </div>
          </div>

          <div className="form-divider">
            <div className="divider-line"></div>
            <span className="divider-text">Liability Waiver</span>
            <div className="divider-line"></div>
          </div>

          <div className="liability-waiver-section">
            <div className="waiver-content">
              <h3>FabMo Liability Waiver and Code of Conduct</h3>
              <div className="waiver-text">
                <h4>1. Photography and Videography</h4>
                <p>I understand that photography and videography are frequently conducted at FabMo for promotional and informational purposes. By allowing myself to be photographed, I understand that I am giving my permission for my image and/or name to be used, without compensation, for the purpose of promoting FabMo and its mission. If I do not wish to be photographed, I will inform the photographer.</p>

                <h4>2. Liability Waiver</h4>
                <p>I hereby waive and release all claims for damages that I may have against FabMo, its officers, agents or governing Board for all injuries and illnesses of every type, suffered by myself or any minors in my care that may happen while at FabMo. I understand that there are certain known and foreseeable risks of physical injury and illness associated with participation at FabMo. I understand that there may be other risks of which I may not be presently aware. I understand that I am free at any time to refuse to do anything at FabMo that I believe poses a hazard to me. By submitting this release, I expressly assume all these risks for myself and/or any minors in my care, and will not make any claims against FabMo, its officers, agents or governing Board.</p>

                <h4>3. Authorization for Medical Treatment</h4>
                <p>In the event a medical emergency renders me unable to give directives or consent, I hereby give my permission to be taken to the nearest available medical facility and have any necessary emergency medical treatment administered. Furthermore, I specifically release all such medical care providers, FabMo and their representatives from any liability in acting in such circumstances.</p>

                <h4>4. Code of Conduct</h4>
                <ul>
                  <li>I understand and will promote a supportive community engaged in furthering FabMo's mission: FabMo promotes and encourages good stewardship of the earth by diverting discontinued designer fabrics, tiles, wallpapers and other similar materials from landfills, making them available to all interested persons and inspiring creative reuse.</li>
                  <li>I will treat all volunteers and community members with kindness, respect, courtesy and dignity, modelling behaviour that recognizes the human rights of all people and is fair, inclusive and respectful of the diversity among the FabMo community.</li>
                  <li>I will follow the rules and policies set forth by the FabMo Board and the instructions of team leaders and volunteer supervisors.</li>
                  <li>I will observe safe work habits and be aware of the safety of others.</li>
                </ul>
                <p>I hereby give my permission for FabMo to collect and store my personal information and to utilize it or share it with team members when necessary for my well being. FabMo promises not to sell or share your information to/with any individual or organization outside FabMo and to protect it from unauthorized use.</p>
                <p>By submitting this form, I am acknowledging the receipt of the information in this form, and I have read, understood and am agreeing to the conditions and terms set out in this form.</p>
              </div>
            </div>

            <div className="waiver-agreement">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="liabilityWaiverAccepted"
                  checked={formData.liabilityWaiverAccepted}
                  onChange={handleInputChange}
                  required
                  className="waiver-checkbox"
                />
                <span className="checkbox-text">
                  I have read and agree to the FabMo Liability Waiver and Code of Conduct
                </span>
              </label>
            </div>
          </div>

          <button
            type="submit"
            className={`submit-button ${isLoading ? 'loading' : ''}`}
            disabled={isLoading}
          >
            {isLoading ? (
              <div className="loading-spinner"></div>
            ) : (
              'Register New User'
            )}
          </button>

          {message && !generatedPin && (
            <div className="message error">
              {message}
            </div>
          )}

          {generatedPin && (
            <div className="pin-success">
              <p>
                Check your email for a welcome message with your User Code.<br />
                If you don't see it, check your spam/junk folder.<br />
                Still missing? Contact <a href="mailto:data@fabmo.org">data@fabmo.org</a>
              </p>
              <p>Your unique 5-digit User Code has been generated! Click the button below to log in.</p>
              <button
                type="button"
                onClick={handleLoginWithPin}
                className="login-button"
              >
                Login with User Code: {generatedPin}
              </button>
            </div>
          )}
        </form>

        <div className="new-user-footer">
          <button onClick={handleBack} className="back-button">
            ← Back to Login
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewUserPage;
